import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { RootState } from "@/store";
import {
  syncSearchHistoryToBackend,
  loadSearchHistoryFromBackend,
} from "@/utils/searchHistory";

/**
 * Hook to automatically sync search history with backend
 * - On login: Load backend history and merge with local
 * - Periodically: Sync unsynced local searches to backend
 * - On logout: Keep local history only
 */
export const useSearchHistorySync = () => {
  const { user, isAuthenticated } = useSelector(
    (state: RootState) => state.auth
  );
  const location = useLocation();

  useEffect(() => {
    // Don't run on auth pages or when not authenticated to prevent errors
    const isAuthPage =
      location.pathname.startsWith("/auth/login") ||
      location.pathname.startsWith("/auth/register") ||
      location.pathname.startsWith("/auth/forgot-password") ||
      location.pathname.startsWith("/auth/reset-password") ||
      location.pathname.startsWith("/auth/activate-account") ||
      location.pathname.startsWith("/auth/otp-verification") ||
      location.pathname.startsWith("/auth"); // Catch-all for any auth routes

    if (isAuthPage || !isAuthenticated || !user?.id) {
      return;
    }

    // On login: Load backend history
    const loadBackendHistory = async () => {
      try {
        await loadSearchHistoryFromBackend(user.id);
      } catch (error) {
        console.error("Failed to load backend search history:", error);
      }
    };

    loadBackendHistory();

    // Immediate sync after load (in case there are unsynced items)
    setTimeout(async () => {
      try {
        await syncSearchHistoryToBackend(user.id);
      } catch (error) {
        console.error("Failed to sync search history:", error);
      }
    }, 2000); // Wait 2 seconds after login

    // Periodic sync: Every 30 seconds for testing
    const syncInterval = setInterval(async () => {
      try {
        await syncSearchHistoryToBackend(user.id);
      } catch (error) {
        console.error("Failed to sync search history:", error);
      }
    }, 30 * 1000); // 30 seconds for testing

    // Cleanup interval on unmount or logout
    return () => {
      clearInterval(syncInterval);
    };
  }, [isAuthenticated, user?.id, location.pathname]);
};
